// The width rules of RightDockPanel. They are pure functions, so a host app can test them without
// a browser. The host apps auto-import this folder, so each name starts with "dockPanel". The
// prefix prevents a conflict with a name of the host.

export interface DockPanelLimits {
  min: number;
  max: number;
}

/** The part of `Storage` that the panel uses. A test can give a plain object. */
export type DockPanelStorage = Pick<Storage, 'getItem' | 'setItem'>;

/**
 * The limits for the current viewport. The rest of the viewport keeps at least `minRestWidth`,
 * but the panel never gets narrower than `minWidth`.
 */
export function dockPanelLimits(
  minWidth: number,
  maxWidth: number,
  viewportWidth: number,
  minRestWidth: number,
): DockPanelLimits {
  const room = viewportWidth - minRestWidth;
  return { min: minWidth, max: Math.max(minWidth, Math.min(maxWidth, room)) };
}

/**
 * Keeps a width inside the limits. A value that is not a finite number gives the minimum, so a
 * bad stored value can never hide the panel or cover the page.
 */
export function dockPanelClampWidth(width: number, limits: DockPanelLimits): number {
  if (!Number.isFinite(width)) return limits.min;
  return Math.round(Math.min(limits.max, Math.max(limits.min, width)));
}

/**
 * The width while the user drags the handle on the left edge. The panel is on the right, so a
 * move to the left makes it wider.
 */
export function dockPanelDragWidth(
  startWidth: number,
  startX: number,
  currentX: number,
  limits: DockPanelLimits,
): number {
  return dockPanelClampWidth(startWidth + (startX - currentX), limits);
}

/**
 * The stored width, or the default width when no valid value is stored. Storage can throw, for
 * example in a private window, and the panel must still open.
 */
export function dockPanelReadWidth(
  storage: DockPanelStorage | null | undefined,
  key: string,
  defaultWidth: number,
  limits: DockPanelLimits,
): number {
  let raw: string | null = null;
  try {
    raw = storage?.getItem(key) ?? null;
  } catch {
    raw = null;
  }
  const stored = raw === null || raw.trim() === '' ? Number.NaN : Number(raw);
  return dockPanelClampWidth(Number.isFinite(stored) ? stored : defaultWidth, limits);
}

/** Stores the clamped width and gives it back. A storage error does not stop the change of width. */
export function dockPanelWriteWidth(
  storage: DockPanelStorage | null | undefined,
  key: string,
  width: number,
  limits: DockPanelLimits,
): number {
  const value = dockPanelClampWidth(width, limits);
  try {
    storage?.setItem(key, String(value));
  } catch {
    // The width then lasts for this page load only.
  }
  return value;
}
